import { useQuery } from '@tanstack/react-query';
import { getDashboardStats, listSessions, type SessionSummary } from '../api/client';
import { useNavigate } from 'react-router-dom';

function StatusBadge({ status }: { status: SessionSummary['status'] }) {
  const classMap: Record<string, string> = {
    active: 'badge badge--green',
    paused: 'badge badge--yellow',
    completed: 'badge badge--gray',
  };
  return <span className={classMap[status] || 'badge'}>{status}</span>;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function DashboardPage() {
  const navigate = useNavigate();

  const statsQuery = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => getDashboardStats(),
  });

  const recentSessionsQuery = useQuery({
    queryKey: ['sessions', 1],
    queryFn: () => listSessions(1, 5),
  });

  const stats = statsQuery.data?.data;
  const recentSessions = recentSessionsQuery.data?.data ?? [];

  return (
    <div className="page">
      <h2>Dashboard</h2>
      <p className="page-subtitle">System overview and quick stats</p>

      <div className="dashboard-stats">
        <div className="stat-card">
          <span className="stat-card-value text-blue">
            {statsQuery.isLoading ? '...' : (stats?.activeSessions ?? 0)}
          </span>
          <span className="stat-card-label">Active Market Sessions</span>
        </div>
        <div className="stat-card">
          <span className="stat-card-value text-green">
            {statsQuery.isLoading ? '...' : (stats?.totalSessions ?? 0)}
          </span>
          <span className="stat-card-label">Total Backtest Simulations</span>
        </div>
        <div className="stat-card">
          <span className="stat-card-value text-purple">
            {statsQuery.isLoading ? '...' : (stats?.totalOrders ?? 0)}
          </span>
          <span className="stat-card-label">Total Filled Orders</span>
        </div>
        <div className="stat-card">
          <span className="stat-card-value text-gold">2025-10-01</span>
          <span className="stat-card-label">Exam Holdout Lock</span>
        </div>
      </div>

      {/* Visual Command & Action Hub */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div
          className="card"
          style={{ cursor: 'pointer', margin: 0 }}
          onClick={() => navigate('/manager')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 24 }}>🧠</span>
            <strong style={{ fontSize: 16, color: 'var(--text-heading)' }}>AI Fund Manager</strong>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            Inspect the 100-level roadmap, 4 risk tiers, macro governors, and 15-year NFP news study.
          </p>
        </div>

        <div
          className="card"
          style={{ cursor: 'pointer', margin: 0 }}
          onClick={() => navigate('/research/exam')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 24 }}>📝</span>
            <strong style={{ fontSize: 16, color: 'var(--text-heading)' }}>Holdout Exam Suite</strong>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            Sit candidate strategies and funds against unrevealed out-of-sample data with automated grading.
          </p>
        </div>

        <div
          className="card"
          style={{ cursor: 'pointer', margin: 0 }}
          onClick={() => navigate('/research/school')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 24 }}>🎓</span>
            <strong style={{ fontSize: 16, color: 'var(--text-heading)' }}>Trading School</strong>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            Explore 243 empirical lessons, multi-market checks, and verified backtest experiments.
          </p>
        </div>
      </div>


      {statsQuery.isError && (
        <div className="error-state">
          Unable to load dashboard stats. The API may be unavailable.
        </div>
      )}

      <div className="card mt-24">
        <h3 className="card-title">Recent Sessions</h3>
        {recentSessionsQuery.isLoading && (
          <div className="loading-state">Loading...</div>
        )}
        {recentSessionsQuery.isError && (
          <div className="error-state">Unable to load recent sessions.</div>
        )}
        {!recentSessionsQuery.isLoading && recentSessions.length === 0 && (
          <div className="empty-state">No sessions yet.</div>
        )}
        {recentSessions.length > 0 && (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Agent ID</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentSessions.map((session) => (
                  <tr
                    key={session.id}
                    className="data-table-row--clickable"
                    onClick={() => navigate(`/sessions/${session.id}`)}
                  >
                    <td className="cell-mono">{session.agentId || 'N/A'}</td>
                    <td><StatusBadge status={session.status} /></td>
                    <td>{formatDateTime(session.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
